<template>
  <v-tabs
    ref="tabs"
    v-model="activeTab"
    slider-color="accent"
    :show-arrows="showArrows"
    active-class="sb-tab-active"
    :class="`sb-tabs ${tabsClass}`"
    grow
  >
    <!-- Used in Email Editor Component -->

    <slot name="tabs-title" />
    <template v-if="tabsClass === 'sb-vertical-tabs'">
      <vuescroll ref="vs" :ops="ops">
        <div class="sb_tabs_sidebar_list">
          <v-tab
            v-for="(tab, idx) in tabs"
            :id="`${removeSpecialCharacters(
              tab.name || tab.title || tab.Nickname
            )}_tab`"
            :key="`tab-${idx}`"
            ripple
          >
            <slot name="title" :tab="tab"> No title provided </slot>
          </v-tab>
        </div>
      </vuescroll>
    </template>
    <!-- Vertical Tab End -->
    <div v-else class="sb_tabs_sidebar_list">
      <v-tab
        v-for="(tab, idx) in tabs"
        :id="`${removeSpecialCharacters(
          tab.name || tab.title || tab.Nickname
        )}_tab`"
        :key="`tab-${idx}`"
        ripple
      >
        <slot name="title" :tab="tab"> No title provided </slot>
      </v-tab>
      <slot name="action" />
    </div>

    <v-tab-item
      v-if="showWarning"
      :key="`tab-item-${tabs.length}`"
      class="no_list_warning px-2 py-2"
    >
      <strong>{{ warningMessage }}</strong>
    </v-tab-item>
    <v-tab-item v-for="(tab, idx) in tabs" :key="`tab-item-${idx}`">
      <slot :name="`content-${idx}`" :tab="tab">
        <slot name="content" :tab="tab"> {{ placeHolderText }} </slot>
      </slot>
    </v-tab-item>
  </v-tabs>
</template>

<script>
const easings = ['easeInQuad'];
import vuescroll from 'vuescroll';
import { arrayOfObjects } from '../static/propValidators.js';
/**
 * This component is used to display tabbed content.
 * To use this component you must provide a v-model for the index of the active
 * tab, and an array of objects for the tabs. The entire object is passed to the
 * both the "title" and "content" slots through a "tab" property on the slot scope.
 *
 * Example usage with no title customization
 * <base-tabs
 *   v-model="activeTab" //the index of the initial active tab, bound value
 *   :tabs="[{ name: 'Tab1' }, { name: 'Tab2' }]" //list of objects for the tabs
 * >
 *   <template #content="{ tab }">
 *     This is the "{{ tab.name}}" tab
 *   </template>
 * </base-tabs>
 */
/*
For more information of implementing v-model on custom components:
https://vuejs.org/v2/guide/components.html#Using-v-model-on-Components
*/
export default {
  name: 'BaseTabs',
  components: {
    vuescroll,
  },
  props: {
    tabsClass: {
      type: String,
      default: 'base-tabs',
    },
    warningMessage: {
      type: String,
      default: 'No Records Found',
    },
    tabs: {
      type: Array,
      required: true,
      validator: arrayOfObjects,
    },
    // v-model for the active tab
    value: {
      type: Number,
      required: true,
    },
    hasTitles: {
      type: Boolean,
      default: true,
    },
    showArrows: {
      type: Boolean,
    },
    showWarning: {
      type: Boolean,
      default: false,
    },
    placeHolderText: {
      type: String,
      default: 'No content provided',
    },
  },
  data() {
    return {
      operation: {
        rail: {
          size: '5px',
        },
        bar: {
          size: '7px',
          opacity: 0.5,
          onlyShowBarOnScroll: false,
        },
      },
      ops: {
        rail: {
          opacity: '0.2',
          background: undefined,
          border: '1px solid #cecece',
          size: '7px',
        },
        bar: {
          background: '#fff',
          keepShow: false,
          size: '7px',
          minSize: 0.5,
        },
        scrollButton: {
          enable: true,
          background: '#cecece',
        },
        scrollPanel: {
          easing: 'easeInQuad',
          speed: 800,
        },
        vuescroll: {
          wheelScrollDuration: 0,
          wheelDirectionReverse: false,
          locking: true,
          checkShifKey: true,
        },
      },
      width: '',
      easings,
      dataCount: 1024,
      items: [],
      // Scroll End
    };
  },
  computed: {
    activeTab: {
      get() {
        return this.value;
      },
      set(newValue) {
        // v-model for active tab
        this.$emit('input', newValue);
      },
    },
  },
  watch: {
    showArrows: function () {
      this.$refs['tabs'].onResize();
    },
  },
  methods: {
    removeSpecialCharacters(str) {
      return str
        .replace(/(?!\w|\s)./g, '')
        .replace(/\s+/g, '')
        .replace(/^(\s*)([\W\w]*)(\b\s*$)/g, '$2');
    },
  },
};
</script>

<style>
@import '../static/css/Components/BaseTabs.css';
</style>
<style scoped>
.no_list_warning {
  height: calc(100vh - 213px);
  align-items: center;
  justify-content: center;
}
</style>
<style lang="scss">
.plain-text-tabs {
  .sb-base-table .v-table__overflow {
    max-height: calc(100vh - 384px);
  }
  .layout {
    margin-top: 0px !important;
  }
}
</style>
